<script setup>
  import ComponentIcon from "@components/Icons/Icon.vue";

  defineOptions({name: "PatientinputComponentStepper"});

  const props = defineProps({
    steps: {
      type: Array,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["step-clicked"]);

  const stepClicked = (stepIndex) => {
    if (!props.disabled) {
      emit("step-clicked", stepIndex + 1);
    }
  };
</script>

<template>
  <nav aria-label="Progress" class="sticky top-0 z-10">
    <ol class="flex items-center justify-center">
      <li
        v-for="({name}, stepIdx) in steps"
        :key="stepIdx"
        :class="[stepIdx !== steps.length - 1 ? 'pr-3 sm:pr-10 md:pr-16' : '', 'relative']"
        class="bg-gray-50 py-3"
        :data-test="getTestId(`stepper-${stepIdx}`)"
      >
        <template v-if="stepIdx + 1 < currentStep">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="h-0.5 w-full bg-mcred" />
          </div>

          <button
            class="relative flex size-7 items-center justify-center rounded-full bg-mcred hover:bg-mcred-450 hover:no-underline sm:size-8"
            :title="name"
            :disabled="disabled"
            :data-test="getTestId(`stepper-${stepIdx}-active-button`)"
            @click="stepClicked(stepIdx)"
          >
            <component-icon class="text-white sm:text-xl">check</component-icon>
            <span class="sr-only">{{ name }}</span>
          </button>
        </template>

        <template v-else-if="stepIdx + 1 === currentStep">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="h-0.5 w-full bg-gray-200" />
          </div>

          <button
            class="relative flex size-7 cursor-default items-center justify-center rounded-full border-2 border-mcred bg-white text-mcred hover:no-underline sm:size-8"
            aria-current="step"
            :title="name"
            :disabled="disabled"
            tabindex="-1"
            :data-test="getTestId(`stepper-${stepIdx}-next-button`)"
          >
            <span aria-hidden="true">{{ stepIdx + 1 }}</span>
            <span class="sr-only">{{ name }}</span>
          </button>
        </template>

        <template v-else>
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="h-0.5 w-full bg-gray-200" />
          </div>

          <button
            class="group relative flex size-7 cursor-default items-center justify-center rounded-full border-2 border-gray-300 bg-white hover:no-underline sm:size-8"
            :title="name"
            :disabled="disabled"
            tabindex="-1"
            :data-test="getTestId(`stepper-${stepIdx}-button`)"
          >
            <span aria-hidden="true" class="text-gray-300">{{ stepIdx + 1 }}</span>
            <span class="sr-only">{{ name }}</span>
          </button>
        </template>
      </li>
    </ol>
  </nav>
</template>
